import React, { memo } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import {
  getPlatformSlug,
  UNAVAILABLE_REASON,
} from '../../../../../features/ecommerces/ecommercesPlatforms';
import {
  selectAvailableEcommerces,
  selectPlatformAvailability,
} from '../../../../../features/ecommerces/ecommercesSelectors';

const REASON_LABELS = {
  [UNAVAILABLE_REASON.INTEGRATED]: 'Ya integrada · Ver',
  [UNAVAILABLE_REASON.ALL_WAREHOUSES]: 'Todas tus bodegas tienen Mercado Libre',
  [UNAVAILABLE_REASON.NO_WAREHOUSES]: 'Necesitas una bodega',
};

/**
 * Picks the platform to integrate. Nothing is saved here: an available platform
 * opens its detail in "new" mode, and one the seller already has opens the
 * existing integration instead of a second that would never receive orders.
 */
const AddEcommerceModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const catalog = useSelector(selectAvailableEcommerces);
  const availability = useSelector(selectPlatformAvailability);

  const handleSelect = (slug) => {
    const { available, existingId } = availability[slug] || {};
    onClose();
    if (available) navigate(`/configuration/ecommerce/new/${slug}`);
    else if (existingId) navigate(`/configuration/ecommerce/${existingId}`);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Agregar integración"
      size="lg"
      testId="ecommerce-add-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        Elige la plataforma de tu tienda. Puedes tener una integración por plataforma; Mercado
        Libre, una por bodega.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {catalog.map((ecommerce) => {
          const slug = getPlatformSlug(ecommerce.name);
          const { available = true, existingId = null, reason = null } = availability[slug] || {};
          const disabled = !available && !existingId;
          const reasonLabel = REASON_LABELS[reason];

          return (
            <button
              key={ecommerce.name}
              type="button"
              data-testid="ecommerce-option"
              data-name={ecommerce.name}
              data-integrated={reason === UNAVAILABLE_REASON.INTEGRATED}
              aria-label={reasonLabel ? `${ecommerce.name}: ${reasonLabel}` : ecommerce.name}
              disabled={disabled}
              onClick={() => handleSelect(slug)}
              className={clsx(
                'flex flex-col items-center gap-2 rounded-2xl border p-4 transition-all text-center',
                'bg-white dark:bg-[#111] disabled:opacity-50 disabled:cursor-not-allowed',
                available
                  ? 'border-border hover:border-ninja-green hover:shadow-md'
                  : 'border-dashed border-border hover:border-text-muted'
              )}
            >
              <div className="h-14 w-full flex items-center justify-center">
                {ecommerce.image ? (
                  <img src={ecommerce.image} alt="" className="max-h-14 w-full object-contain" />
                ) : (
                  <span className="text-2xl font-black text-text-muted">{ecommerce.name[0]}</span>
                )}
              </div>
              <span className="text-sm font-bold text-ninja-dark dark:text-white">{ecommerce.name}</span>
              {reasonLabel && (
                <span className="text-[11px] font-semibold text-text-secondary leading-tight">
                  {reasonLabel}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex justify-end mt-6">
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </Modal>
  );
};

export default memo(AddEcommerceModal);
